import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Form } from "react-bootstrap";
import classNames from "classnames";
import { Link } from "gatsby-link";
import { Download } from "react-bootstrap-icons";

import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import { isValidEmail, isValidName } from "../utils/utils";
import { BUSINESS_TYPES, GATED_FORM_SOURCE_TITLE } from "../utils/common";

import ShiftDashes from "../images/icons/shift-dashes.svg";

import "../styles/components/BusinessInfoForm.scss";

const BusinessInfoForm = ({
    contentDescription,
    download = false,
    link,
    source,
}) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [businessType, setBusinessType] = useState("");
    const [company, setCompany] = useState("");
    const [jobTitle, setJobTitle] = useState("");
    const [privacyCheckbox, setPrivacyCheckbox] = useState(false);

    //form state
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const title = GATED_FORM_SOURCE_TITLE[source];

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updateEmail = e => {
        setEmail(e.target.value);
        setValidity("email", isValidEmail(e.target.value));
    };

    const isValid = () => {
        const _errors = {
            name: !isValidName(name),
            email: !isValidEmail(email),
            businessType: !businessType,
            company: !isValidName(company),
            jobTitle: !isValidName(jobTitle),
        };
        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { businessInfo: endpointUrl },
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            name,
            email,
            business_type: businessType,
            company,
            job_title: jobTitle,
            content_url: link,
            content_description: contentDescription,
            recaptcha_token: reCAPTCHAToken,
            referer: window?.location?.href || "",
        };
        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        try {
            const token = await reCAPTCHARef.current.executeAsync();
            submitForm(token);
        } catch (error) {
            console.error(error);
        }
    };

    if (submitted && !submissionError) {
        let btnProps;

        if (link.startsWith("/")) {
            btnProps = {
                as: Link,
                to: link,
            };
        } else {
            btnProps = {
                as: "a",
                href: link,
                download: !!download,
                target: "_blank",
                rel: "noreferrer",
            };
        }

        return (
            <div className="business-info-form">
                <div className="form-header pt-5">
                    <h3>Thank You for Connecting!</h3>
                    <ShiftDashes className="shift-dashes" />
                </div>

                <div className="success-result">
                    <p>
                        {download
                            ? "Click the button below to download the PDF."
                            : `Click the button below to ${title}`}
                    </p>

                    <div>
                        <Button {...btnProps}>
                            {download ? (
                                title
                            ) : (
                                <>
                                    <Download
                                        size="20"
                                        color="white"
                                        className="me-2"
                                    />{" "}
                                    Download
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="business-info-form">
            <div className="form-header pt-5">
                <h3>{title}</h3>
                <ShiftDashes className="shift-dashes" />
            </div>
            <Form
                className={classNames("form", {
                    "has-submit-attempt": hasSubmitAttempt,
                })}
                onSubmit={executeReCAPTCHA}
                key="blog-post-form"
            >
                <Form.Group className="mb-4" controlId="name">
                    <Form.Control
                        type="text"
                        placeholder="Name"
                        size="lg"
                        required
                        value={name}
                        onChange={e => setName(e.target.value)}
                        className={classNames({
                            invalid: !!errors["name"],
                        })}
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="email">
                    <Form.Control
                        type="email"
                        placeholder="Email"
                        size="lg"
                        required
                        value={email}
                        onChange={updateEmail}
                        className={classNames({
                            invalid: !!errors["email"],
                        })}
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="business-type">
                    <Form.Select
                        required
                        size="lg"
                        placeholder="Business type"
                        defaultValue={businessType}
                        onChange={e => setBusinessType(e.target.value)}
                        className={classNames({
                            "select-placeholder": !businessType,
                            invalid: !!errors["businessType"],
                        })}
                    >
                        <option disabled value="">
                            Business Type
                        </option>
                        {BUSINESS_TYPES.map(business => (
                            <option value={business} key={business}>
                                {business}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-4" controlId="company">
                    <Form.Control
                        type="text"
                        placeholder="Company"
                        size="lg"
                        required
                        value={company}
                        onChange={e => setCompany(e.target.value)}
                        className={classNames({
                            invalid: !!errors["company"],
                        })}
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="jobTitle">
                    <Form.Control
                        type="text"
                        placeholder="Job Title"
                        size="lg"
                        required
                        value={jobTitle}
                        onChange={e => setJobTitle(e.target.value)}
                        className={classNames({
                            invalid: !!errors["jobTitle"],
                        })}
                    />
                </Form.Group>
                <Form.Group className="mb-4" controlId="privacy-check">
                    <Form.Check size="lg" type="checkbox" className="ms-1">
                        <Form.Check.Input
                            type="checkbox"
                            required
                            checked={privacyCheckbox}
                            onChange={e => setPrivacyCheckbox(e.target.checked)}
                        />
                        <Form.Check.Label className="compliance ms-1">
                            I understand and agree to the terms of ShiftMed's{" "}
                            <Link to="/privacy/" target="privacy">
                                Privacy Policy.
                            </Link>
                        </Form.Check.Label>
                    </Form.Check>
                </Form.Group>

                <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={submitting}
                    className="px-5"
                >
                    {title}
                </Button>

                {submissionError && (
                    <Alert variant="danger">{submissionError}</Alert>
                )}

                <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
            </Form>
        </div>
    );
};

BusinessInfoForm.propTypes = {
    contentDescription: PropTypes.string,
    link: PropTypes.string.isRequired,
    download: PropTypes.bool,
    source: PropTypes.string.isRequired,
};

export default BusinessInfoForm;
